/**
 * PokerTable — the new SpotCard visual layer. An oval table with 9 seats
 * around the perimeter, chips for current-street wagers, dealer button,
 * pot label + board cards + action history in the center, hero hand at
 * the bottom.
 *
 * Pure presentation: receives a `SpotPayload`, returns the rendered table.
 * No callbacks, no action handlers — those live in TableActionBar, which
 * the parent SpotCard composes below this table.
 *
 * Seat positioning math lives in `lib/seatLayout.ts`; the natural-language
 * history narrative lives in `lib/handHistoryNL.ts`. Both are pure +
 * tested in isolation, so this file is mostly layout glue + styling.
 */

import {
  chipPositionPct,
  dealerButtonSeatIndex,
  POSITIONS_CLOCKWISE,
  rotateSeats,
  seatPositionPct,
  type Position,
} from "@/features/chat/lib/seatLayout";
import { formatBb } from "@/shared/format";
import type { SpotPayload } from "@/shared/models";
import { PlayingCard } from "@/features/chat/components/PlayingCard";

interface PokerTableProps {
  spot: SpotPayload;
}

export function PokerTable({ spot }: PokerTableProps) {
  const rotated = rotateSeats(spot.position);
  const heroPosition = spot.position;
  const dealerIdx = dealerButtonSeatIndex(heroPosition);
  const foldedSet = computeFoldedPositions(spot);
  const wagers = computeCurrentStreetWagers(spot);
  // Every seat that's still in the hand gets the bright accent + pulse
  // — hero AND active villains, with no asymmetry. Folded seats fade
  // to opacity 0.28. The `facingVillain` seat (last non-Hero
  // raiser/bettor) is the only villain that carries a stack number,
  // since the spot payload models stacks as `{hero, villain}` (one
  // villain stack value). Hero's own stack always renders.
  const facingVillain = facingVillainPosition(spot);

  return (
    <div
      className="relative w-full mx-auto aspect-[0.85/1] md:aspect-[1.4/1]"
      style={{
        maxWidth: "620px",
      }}
    >
      {/* Felt — the oval itself. Radial gradient for subtle depth, bright
       *  blue rim for the "premium" feel, drop shadow for elevation.
       *  Border + glow tuned to read as "lit-up felt" on dark surface
       *  rather than a flat outline. */}
      <div
        className="absolute inset-0 rounded-[50%/35%]"
        style={{
          background:
            "radial-gradient(ellipse at 50% 45%, #0d1c3a 0%, #050811 80%)",
          border: "2px solid #5e7be8",
          boxShadow:
            "inset 0 0 50px rgba(124, 147, 241, 0.22), 0 0 24px rgba(124, 147, 241, 0.18), 0 28px 60px -24px rgba(0, 0, 0, 0.85)",
        }}
      />

      {/* Seat ring — 9 seat pills around the perimeter. Hero stays
       *  subdued (i === 0 → no accent). All non-folded villains get
       *  the bright accent + pulse. Only the facing villain shows a
       *  stack number (single-villain data model). */}
      {rotated.map((position, i) => {
        const isHero = i === 0;
        const isFolded = foldedSet.has(position);
        const isFacingVillain = !isHero && position === facingVillain;
        // `spot.stacks` is the STARTING stack; what's displayed behind a
        // seat is the REMAINING stack = starting − everything that player
        // has committed across the streets so far (so a jam shows 0/all-in,
        // not the full starting stack).
        const startBb = isHero
          ? spot.stacks.hero
          : isFacingVillain
            ? spot.stacks.villain
            : undefined;
        const stackBb =
          startBb === undefined
            ? undefined
            : Math.max(0, startBb - committedByPosition(spot, position));
        return (
          <Seat
            key={position}
            position={position}
            index={i}
            isHero={isHero}
            isFolded={isFolded}
            stackBb={stackBb}
          />
        );
      })}

      {/* Dealer button — small yellow "D" next to the BTN seat. When
       *  hero IS the BTN (dealerIdx === 0), the seat sits where the
       *  hero hand is, so we offset the button to the seat's right
       *  rather than between seat and table center (which would put it
       *  behind the cards). */}
      <DealerButton seatIndex={dealerIdx} heroIsBtn={dealerIdx === 0} />

      {/* Chip stacks — one per actor with a current-street wager.
       *  Hero (seatIdx 0) is special-cased: hero seat sits at the bottom
       *  and the hero hand cards are directly above it, so a chip placed
       *  on the seat-to-center radius would render behind the cards.
       *  We shift hero's chip to the right of the hand instead. */}
      {wagers.map(({ position, amount }) => {
        const seatIdx = rotated.indexOf(position as Position);
        if (seatIdx < 0) return null;
        return (
          <ChipStack
            key={position}
            seatIndex={seatIdx}
            amount={amount}
            isHero={seatIdx === 0}
          />
        );
      })}

      {/* Center column: pot label (upper-center) + board cards (centered)
       *  + hero hand (above bottom seat). Action-history narrative now
       *  lives OUTSIDE the table — rendered in SpotCard below the
       *  action buttons — so the felt itself stays clean. */}
      <PotLabel value={spot.pot} />
      {spot.board.length > 0 && <BoardCards cards={spot.board} />}
      <HeroHand cards={spot.hand} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seat — single position pill, anchored at the geometric position computed
// from seatLayout. Hero gets an accent ring; folded seats fade out.
// ---------------------------------------------------------------------------

interface SeatProps {
  position: Position;
  index: number;
  isHero: boolean;
  isFolded: boolean;
  stackBb: number | undefined;
}

function Seat({ position, index, isHero, isFolded, stackBb }: SeatProps) {
  // isHero is retained for future visual differentiation (e.g. a
  // subtle "you" marker) but is intentionally unused in the highlight
  // logic — hero and active villains get the identical treatment so a
  // glance immediately reads "these seats are still in the hand."
  void isHero;
  const { left, top } = seatPositionPct(index);
  const isActive = !isFolded;
  return (
    <div
      className="absolute select-none"
      style={{
        left: `${left}%`,
        top: `${top}%`,
        transform: "translate(-50%, -50%)",
        opacity: isFolded ? 0.28 : 1,
        transition: "opacity 200ms",
      }}
    >
      <div
        className={[
          "rounded-full px-2.5 py-1 text-center min-w-[46px]",
          "bg-bg/95 backdrop-blur-sm",
          isActive
            ? "border-2 border-accent text-text shadow-[0_0_16px_rgba(124,147,241,0.55)] pt-pulse"
            : "border border-accent/40 text-text-muted",
        ].join(" ")}
        style={{
          fontSize: "clamp(10px, 2.4vw, 11px)",
          lineHeight: 1.15,
        }}
      >
        <div className="font-semibold tracking-wide">{position}</div>
        {stackBb !== undefined && (
          <div
            className="font-mono tabular-nums mt-0.5 text-[0.85em]"
            style={{
              color: isActive
                ? "var(--color-accent)"
                : "var(--color-text-muted)",
            }}
          >
            {formatBb(stackBb)}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DealerButton — small "D" disc anchored next to the BTN seat. Sits between
// the BTN seat and the center, slightly offset so it doesn't overlap.
// ---------------------------------------------------------------------------

function DealerButton({
  seatIndex,
  heroIsBtn,
}: {
  seatIndex: number;
  heroIsBtn: boolean;
}) {
  const seat = seatPositionPct(seatIndex);
  let left: number;
  let top: number;
  if (heroIsBtn) {
    // Hero seat lives at bottom-center and is competing for space with
    // the hero hand cards. Put the dealer button to the RIGHT of the
    // hero seat pill (offset along the +x axis), at the same vertical
    // level as the seat.
    left = seat.left + 8;
    top = seat.top;
  } else {
    // Standard placement: between the BTN seat and the table center.
    left = 50 + (seat.left - 50) * 0.82;
    top = 50 + (seat.top - 50) * 0.82;
  }
  return (
    <div
      className="absolute select-none flex items-center justify-center font-bold text-[10px]"
      style={{
        left: `${left}%`,
        top: `${top}%`,
        width: "20px",
        height: "20px",
        marginLeft: "-10px",
        marginTop: "-10px",
        borderRadius: "50%",
        background: "#facc15",
        color: "#1a1a1a",
        boxShadow: "0 2px 4px rgba(0,0,0,0.4)",
      }}
    >
      D
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChipStack — single small chip with the bet amount labeled next to it.
// Placed in front of the seat (between seat and pot center).
// ---------------------------------------------------------------------------

function ChipStack({
  seatIndex,
  amount,
  isHero,
}: {
  seatIndex: number;
  amount: number;
  isHero: boolean;
}) {
  // Hero chip needs a manual position so it doesn't collide with the
  // hero hand cards (which live at bottom ~22% / top ~78%). Tucking
  // the chip to the right of the cards at the same vertical level
  // keeps it physically meaningful (still on hero's side of the felt)
  // without overlapping the cards.
  const { left, top } = isHero ? { left: 66, top: 81 } : chipPositionPct(seatIndex);
  return (
    <div
      className="absolute flex items-center gap-1.5 select-none"
      style={{
        left: `${left}%`,
        top: `${top}%`,
        transform: "translate(-50%, -50%)",
      }}
    >
      <div
        className="rounded-full"
        style={{
          width: "12px",
          height: "12px",
          background: "var(--color-accent)",
          border: "2px solid #ffffff20",
          boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
        }}
      />
      <div
        className="font-mono tabular-nums text-text font-semibold"
        style={{
          fontSize: "clamp(11px, 2.2vw, 12px)",
          textShadow: "0 1px 2px rgba(0,0,0,0.7)",
        }}
      >
        {formatBb(amount)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Center pieces — pot, board, history
// ---------------------------------------------------------------------------

function PotLabel({ value }: { value: number }) {
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 select-none text-center"
      style={{ top: "26%" }}
    >
      <div
        className="uppercase tracking-[0.15em] text-text-muted/70"
        style={{ fontSize: "clamp(10px, 1.8vw, 10px)" }}
      >
        Pot
      </div>
      <div
        className="font-mono tabular-nums text-text font-semibold mt-0.5"
        style={{ fontSize: "clamp(16px, 3vw, 18px)" }}
      >
        {formatBb(value)}
      </div>
    </div>
  );
}

function BoardCards({ cards }: { cards: string[] }) {
  return (
    <div
      className="absolute left-1/2 flex gap-1.5"
      style={{ top: "50%", transform: "translate(-50%, -50%)" }}
    >
      {cards.map((c, i) => (
        <PlayingCard key={`${c}-${i}`} token={c} size="sm" />
      ))}
    </div>
  );
}

function HeroHand({ cards }: { cards: string[] }) {
  return (
    <div
      className="absolute left-1/2 flex gap-1.5 z-10"
      style={{ bottom: "22%", transform: "translateX(-50%)" }}
    >
      {cards.map((c, i) => (
        <PlayingCard key={`${c}-${i}`} token={c} size="sm" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Derived data — folded set + current-street wagers
// ---------------------------------------------------------------------------

/** Positions considered folded / out of the hand for visual purposes.
 *
 *  Action-aware rule — `POSITIONS_CLOCKWISE` happens to be in preflop
 *  action order (UTG acts first, BB acts last), so its index is the
 *  action-order index too.
 *
 *  - Hero is always active.
 *  - A seat with an explicit fold action in history is folded.
 *  - A seat with any non-fold action in history is active.
 *  - A seat with NO recorded action:
 *      - Preflop: folded iff it acts BEFORE hero in action order
 *        (the action passed them and they didn't enter the pot).
 *        Seats AFTER hero stay active — they haven't had a chance to
 *        act yet, so they're still in the hand (typical "folded
 *        around to CO" pattern — BTN/SB/BB are still live).
 *      - Postflop: folded (any seat not in preflop history must have
 *        folded preflop to disappear).
 *
 *  Exported for tests in PokerTable.test.ts. */
export function computeFoldedPositions(spot: SpotPayload): Set<string> {
  const heroPos = spot.position;
  const seenActive = new Set<string>([heroPos]);
  const explicitFolds = new Set<string>();
  for (const h of spot.history) {
    const actor = h.actor === "Hero" ? heroPos : h.actor;
    if (h.action === "fold") {
      explicitFolds.add(actor);
    } else {
      seenActive.add(actor);
    }
  }
  const folded = new Set<string>(explicitFolds);
  const heroActionIdx = POSITIONS_CLOCKWISE.indexOf(heroPos as Position);
  const isPreflop = spot.street === "preflop";

  // Preflop: once the betting re-raises and the action wraps BACK to hero —
  // hero has already acted (opened/3-bet) and now faces a LATER non-hero
  // raise, i.e. a 3-bet/4-bet/5-bet pot — there are no "yet to act" seats
  // left. Everyone not in the hand has folded. Without this, seats that act
  // after hero in order (CO/BTN/SB) stay lit in a 4-bet pot even though
  // they're long gone. (When hero is still facing the OPEN for the first
  // time, seats after hero genuinely haven't acted — use the order rule.)
  let actionWrapped = false;
  if (isPreflop) {
    let heroLastIdx = -1;
    for (let i = 0; i < spot.history.length; i++) {
      if (spot.history[i]!.actor === "Hero") heroLastIdx = i;
    }
    if (heroLastIdx >= 0) {
      for (let i = heroLastIdx + 1; i < spot.history.length; i++) {
        const h = spot.history[i]!;
        if (h.actor !== "Hero" && h.action === "raise") {
          actionWrapped = true;
          break;
        }
      }
    }
  }

  for (const p of POSITIONS_CLOCKWISE) {
    if (seenActive.has(p)) continue;
    if (folded.has(p)) continue;
    if (!isPreflop || actionWrapped) {
      folded.add(p);
    } else {
      const seatActionIdx = POSITIONS_CLOCKWISE.indexOf(p);
      if (seatActionIdx < heroActionIdx) {
        folded.add(p);
      }
      // else: acts after hero, still to act — leave active
    }
  }
  return folded;
}

/** Total chips a `position` has committed across ALL streets — used to
 *  derive the REMAINING stack (starting − committed) shown behind a seat.
 *
 *  Bet/raise amounts are cumulative "to" levels (e.g. "4-bet to 25"), so a
 *  player's commitment on a street is the final level they reached, summed
 *  across streets. A **call** carries no "to" amount of its own, so it's
 *  resolved against the running bet level on that street — a caller matches
 *  the current bet (e.g. UTG opens to 3, hero 3-bets to 12, UTG calls →
 *  UTG has committed 12, not 3). Implicit blinds are folded in preflop.
 *
 *  Exported for tests in PokerTable.test.ts. */
export function committedByPosition(spot: SpotPayload, position: string): number {
  const heroPos = spot.position;
  const perStreet: Record<string, number> = {};
  // The bet level a caller would have to match, per street. Preflop opens at
  // the 1bb big blind; raises/bets push it up as we walk the history in order.
  const runningLevel: Record<string, number> = { preflop: 1 };
  for (const h of spot.history) {
    const actor = h.actor === "Hero" ? heroPos : h.actor;
    const street = h.street ?? spot.street;
    const isAggressive = h.action === "raise" || h.action === "bet";
    if (isAggressive && h.amount && h.amount > 0) {
      runningLevel[street] = Math.max(runningLevel[street] ?? 0, h.amount);
    }
    if (actor !== position) continue;
    if (isAggressive && h.amount && h.amount > 0) {
      perStreet[street] = Math.max(perStreet[street] ?? 0, h.amount);
    } else if (h.action === "call") {
      // Match the current bet level (its own amount is unreliable/absent).
      const level = Math.max(
        runningLevel[street] ?? 0,
        h.amount && h.amount > 0 ? h.amount : 0,
      );
      perStreet[street] = Math.max(perStreet[street] ?? 0, level);
    }
  }
  // Implicit blinds — never in history, only relevant preflop, subsumed by
  // any larger preflop commitment.
  if (position === "BB") {
    perStreet["preflop"] = Math.max(perStreet["preflop"] ?? 0, 1);
  } else if (position === "SB") {
    perStreet["preflop"] = Math.max(perStreet["preflop"] ?? 0, 0.5);
  }
  return Object.values(perStreet).reduce((sum, v) => sum + v, 0);
}

/** Per-actor wager amount on the CURRENT street. Each actor's value is
 *  the max amount they've committed on this street (their final commit
 *  level after potentially multiple actions). For preflop, the blinds
 *  are added implicitly since they're never represented in history. */
function computeCurrentStreetWagers(
  spot: SpotPayload,
): { position: string; amount: number }[] {
  const street = spot.street;
  const heroPos = spot.position;
  const max: Record<string, number> = {};

  for (const h of spot.history) {
    const entryStreet = h.street ?? street;
    if (entryStreet !== street) continue;
    if (h.amount == null || h.amount <= 0) continue;
    const actor = h.actor === "Hero" ? heroPos : h.actor;
    max[actor] = Math.max(max[actor] ?? 0, h.amount);
  }

  if (street === "preflop") {
    // Implicit blinds — show only if not already set by an action AND
    // the position is still in the hand (no fold). Use a conservative
    // approach: always show them unless they're already represented.
    if (max["BB"] === undefined) max["BB"] = 1;
    if (max["SB"] === undefined) max["SB"] = 0.5;
  }

  return Object.entries(max).map(([position, amount]) => ({ position, amount }));
}

// ---------------------------------------------------------------------------
// Facing-villain detection — same walk as SpotCard.isFacingBet, but
// returns the position label of the most recent non-Hero aggressor
// instead of a boolean. Used to pick which villain seat carries the
// (single) `stacks.villain` number. Returns null if hero faces no
// villain action yet (e.g. preflop first-to-act).
// ---------------------------------------------------------------------------

function facingVillainPosition(spot: SpotPayload): string | null {
  for (let i = spot.history.length - 1; i >= 0; i--) {
    const h = spot.history[i]!;
    if (h.actor === "Hero") continue;
    if (h.action === "bet" || h.action === "raise") return h.actor;
    if (h.action === "check") return null;
  }
  return null;
}
